import React, { useState } from 'react';
import {
  Filter,
  UserPlus,
  Download,
  MoreVertical,
  Phone,
  Mail,
  MapPin,
  X,
  Check,
  Edit2,
  Trash2,
  CheckSquare,
  Square,
  Award,
  BookOpen,
  Calendar,
  MessageSquare,
  ArrowUpRight,
  ShieldAlert,
} from 'lucide-react';
import { AdminStudent, AdminRole } from '../types';
import { Badge, Button, Input, Search, Tooltip } from '../../components/core';

interface StudentManagementProps {
  students: AdminStudent[];
  currentRole: AdminRole;
  onAddStudent: (student: AdminStudent) => void;
  onUpdateStudent: (student: AdminStudent) => void;
  onDeleteStudent: (id: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const StudentManagement: React.FC<StudentManagementProps> = ({
  students,
  currentRole,
  onAddStudent,
  onUpdateStudent,
  onDeleteStudent,
  addToast,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [batchFilter, setBatchFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Bulk selection
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

  // Modals
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<AdminStudent | null>(null);
  const [viewingProfileStudent, setViewingProfileStudent] = useState<AdminStudent | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    photoUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200',
    dob: '2015-01-01',
    gender: 'Male' as 'Male' | 'Female',
    mobile: '',
    parentMobile: '',
    email: '',
    address: '',
    level: 'Level 1 - Prarambhik',
    batch: 'Batch A - Weekend Morning',
    isAttendingPathshala: true,
  });

  // Filter logic
  const filteredStudents = students.filter((s) => {
    const matchesSearch =
      `${s.firstName} ${s.surname}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.parentMobile.includes(searchQuery);

    const matchesLevel = levelFilter === 'ALL' || s.level === levelFilter;
    const matchesBatch = batchFilter === 'ALL' || s.batch === batchFilter;
    const matchesStatus = statusFilter === 'ALL' || s.status === statusFilter;

    return matchesSearch && matchesLevel && matchesBatch && matchesStatus;
  });

  const isAuditor = currentRole === 'Auditor';

  // Handlers
  const handleSelectAll = () => {
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map((s) => s.id));
    }
  };

  const handleToggleSelect = (id: string) => {
    if (selectedStudentIds.includes(id)) {
      setSelectedStudentIds(selectedStudentIds.filter((sid) => sid !== id));
    } else {
      setSelectedStudentIds([...selectedStudentIds, id]);
    }
  };

  const openAddModal = () => {
    setEditingStudent(null);
    setFormData({
      firstName: '',
      surname: '',
      photoUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200',
      dob: '2015-01-01',
      gender: 'Male',
      mobile: '',
      parentMobile: '',
      email: '',
      address: '',
      level: 'Level 1 - Prarambhik',
      batch: 'Batch A - Weekend Morning',
      isAttendingPathshala: true,
    });
    setIsAddEditModalOpen(true);
  };

  const openEditModal = (stu: AdminStudent) => {
    if (isAuditor) {
      addToast('warning', 'Access Restricted', 'View-Only Auditors cannot edit records.');
      return;
    }
    setEditingStudent(stu);
    setFormData({
      firstName: stu.firstName,
      surname: stu.surname,
      photoUrl: stu.photoUrl,
      dob: stu.dob,
      gender: stu.gender,
      mobile: stu.mobile,
      parentMobile: stu.parentMobile,
      email: stu.email,
      address: stu.address,
      level: stu.level,
      batch: stu.batch,
      isAttendingPathshala: stu.isAttendingPathshala,
    });
    setIsAddEditModalOpen(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    if (editingStudent) {
      const updated: AdminStudent = {
        ...editingStudent,
        ...formData,
      };
      onUpdateStudent(updated);
      addToast('success', 'Student Updated', `Successfully updated profile for ${updated.firstName} ${updated.surname}`);
    } else {
      const newStu: AdminStudent = {
        id: `STU-${Math.floor(100 + Math.random() * 900)}`,
        ...formData,
        attendanceRate: 100,
        totalGathas: 0,
        pointsBalance: 50,
        status: 'Active',
        registrationDate: new Date().toISOString().split('T')[0],
      };
      onAddStudent(newStu);
      addToast('success', 'Student Created', `New student ${newStu.firstName} ${newStu.surname} enrolled with ID ${newStu.id}`);
    }
    setIsAddEditModalOpen(false);
  };

  // Bulk Operations
  const handleBulkStatusToggle = () => {
    if (isAuditor) return;
    selectedStudentIds.forEach((id) => {
      const stu = students.find((s) => s.id === id);
      if (stu) {
        onUpdateStudent({
          ...stu,
          status: stu.status === 'Active' ? 'Inactive' : 'Active',
        });
      }
    });
    addToast('success', 'Bulk Action Complete', `Toggled status for ${selectedStudentIds.length} selected students.`);
    setSelectedStudentIds([]);
  };

  const handleBulkCSVExport = () => {
    const csvHeader = 'ID,First Name,Surname,Mobile,Parent Mobile,Level,Batch,Attendance %,Points,Status\n';
    const csvRows = filteredStudents
      .map(
        (s) =>
          `${s.id},${s.firstName},${s.surname},${s.mobile},${s.parentMobile},"${s.level}","${s.batch}",${s.attendanceRate},${s.pointsBalance},${s.status}`
      )
      .join('\n');

    const blob = new Blob([csvHeader + csvRows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Pathshala_Student_Directory_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    addToast('info', 'CSV Download Initiated', `Exported ${filteredStudents.length} student records.`);
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Student Management Directory</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" leftIcon={Download} onClick={handleBulkCSVExport}>
            Export CSV
          </Button>
          {!isAuditor && (
            <Button leftIcon={UserPlus} onClick={openAddModal}>
              Enroll New Student
            </Button>
          )}
        </div>
      </div>

      {/* Filter & Search Controls */}
      <div className="admin-card space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by Name, ID or Mobile..."
          />

          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Academic Levels</option>
            <option value="Level 1 - Prarambhik">Level 1 - Prarambhik</option>
            <option value="Level 2 - Madhyamik">Level 2 - Madhyamik</option>
            <option value="Level 3 - Shravak Junior">Level 3 - Shravak Junior</option>
          </select>

          <select
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
            className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Batches</option>
            <option value="Batch A - Weekend Morning">Batch A - Weekend Morning</option>
            <option value="Batch B - Weekend Evening">Batch B - Weekend Evening</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Statuses</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive Only</option>
          </select>
        </div>

        {/* Bulk Action Strip */}
        {selectedStudentIds.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border border-rose-200/80 bg-rose-50/80 p-3 text-xs text-[var(--gv-brand)]">
            <span className="font-semibold">
              {selectedStudentIds.length} student(s) selected
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleBulkStatusToggle}>
                Toggle Status (Active/Inactive)
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setSelectedStudentIds([])}>
                Deselect All
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Roster Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
            <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
              <tr>
                <th className="w-10 px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-[var(--gv-border)] text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                  />
                </th>
                <th className="px-4 py-2.5 font-bold">Student ID & Name</th>
                <th className="px-4 py-2.5 font-bold">Parent Contact</th>
                <th className="px-4 py-2.5 font-bold">Level & Batch</th>
                <th className="px-4 py-2.5 text-center font-bold">Attendance Rate</th>
                <th className="px-4 py-2.5 text-center font-bold">Gathas</th>
                <th className="px-4 py-2.5 text-center font-bold">Points</th>
                <th className="px-4 py-2.5 font-bold">Status</th>
                <th className="px-4 py-2.5 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white font-medium">
              {filteredStudents.map((stu) => {
                const isSelected = selectedStudentIds.includes(stu.id);
                return (
                  <tr key={stu.id} className={`transition-colors hover:bg-stone-50/80 ${isSelected ? 'bg-rose-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(stu.id)}
                        className="rounded border-[var(--gv-border)] text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={stu.photoUrl} alt={stu.firstName} className="h-9 w-9 shrink-0 rounded-full border border-[var(--gv-border)] object-cover" />
                        <div>
                          <div
                            onClick={() => setViewingProfileStudent(stu)}
                            className="flex cursor-pointer items-center gap-1 font-bold text-[var(--gv-ink)] hover:text-[var(--gv-brand)]"
                          >
                            <span>{stu.firstName} {stu.surname}</span>
                          </div>
                          <span className="font-mono text-[10px] font-semibold text-[var(--gv-brand)]">{stu.id}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--gv-ink)]">{stu.parentMobile}</div>
                      <div className="max-w-[150px] truncate text-[10px] text-[var(--gv-ink-muted)]">{stu.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--gv-ink)]">{stu.level}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">{stu.batch}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge
                        variant={
                          stu.attendanceRate >= 90
                            ? 'success'
                            : stu.attendanceRate >= 75
                            ? 'warning'
                            : 'danger'
                        }
                        className="font-mono normal-case"
                      >
                        {stu.attendanceRate}%
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-bold text-[var(--gv-ink-muted)]">
                      {stu.totalGathas}
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-bold text-amber-700">
                      {stu.pointsBalance} pts
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={stu.status === 'Active' ? 'success' : 'outline'}>
                        {stu.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewingProfileStudent(stu)}
                        >
                          Profile
                        </Button>
                        {!isAuditor && (
                          <>
                            <Tooltip content="Edit student">
                              <Button
                                variant="ghost"
                                size="icon"
                                leftIcon={Edit2}
                                aria-label={`Edit ${stu.firstName} ${stu.surname}`}
                                onClick={() => openEditModal(stu)}
                              />
                            </Tooltip>
                            <Tooltip content="Delete student">
                              <Button
                                variant="ghost"
                                size="icon"
                                leftIcon={Trash2}
                                aria-label={`Delete ${stu.firstName} ${stu.surname}`}
                                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                                onClick={() => onDeleteStudent(stu.id)}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Student Modal */}
      {isAddEditModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card max-h-[90vh] w-full max-w-2xl space-y-5 overflow-y-auto p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingStudent ? `Edit Student: ${editingStudent.firstName} ${editingStudent.surname}` : 'Enroll New Student'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsAddEditModalOpen(false)}
              />
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="First Name"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
                <Input
                  label="Surname"
                  required
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Date of Birth"
                  type="date"
                  required
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                />
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value as 'Male' | 'Female' })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <Input
                  label="Parent Mobile"
                  required
                  value={formData.parentMobile}
                  onChange={(e) => setFormData({ ...formData, parentMobile: e.target.value })}
                  placeholder="+91 98200 00000"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Student Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="student@gmail.com"
                />
                <Input
                  label="Profile Photo URL"
                  value={formData.photoUrl}
                  onChange={(e) => setFormData({ ...formData, photoUrl: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Academic Level Allocation</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Level 1 - Prarambhik">Level 1 - Prarambhik</option>
                    <option value="Level 2 - Madhyamik">Level 2 - Madhyamik</option>
                    <option value="Level 3 - Shravak Junior">Level 3 - Shravak Junior</option>
                  </select>
                </div>
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Batch Slot Allocation</label>
                  <select
                    value={formData.batch}
                    onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Batch A - Weekend Morning">Batch A - Weekend Morning</option>
                    <option value="Batch B - Weekend Evening">Batch B - Weekend Evening</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-[var(--gv-ink)]">Residential Address</label>
                <textarea
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  placeholder="Full home address..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={() => setIsAddEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Student Record</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Viewing Detailed Profile Modal */}
      {viewingProfileStudent && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card max-h-[90vh] w-full max-w-2xl space-y-6 overflow-y-auto p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-start justify-between border-b border-[var(--gv-border)] pb-4">
              <div className="flex items-center gap-4">
                <img
                  src={viewingProfileStudent.photoUrl}
                  alt={viewingProfileStudent.firstName}
                  className="h-16 w-16 rounded-xl border-2 border-[var(--gv-brand)]/30 object-cover shadow-xs"
                />
                <div>
                  <h2 className="text-lg font-bold text-[var(--gv-ink)]">
                    {viewingProfileStudent.firstName} {viewingProfileStudent.surname}
                  </h2>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[var(--gv-brand)]">{viewingProfileStudent.id}</span>
                    <Badge variant={viewingProfileStudent.status === 'Active' ? 'success' : 'outline'}>
                      {viewingProfileStudent.status}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setViewingProfileStudent(null)}
              />
            </div>

            {/* Parent Communication Triggers */}
            <div className="grid grid-cols-3 gap-2 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3 text-center">
              <a
                href={`tel:${viewingProfileStudent.parentMobile}`}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-[var(--gv-border)] bg-white p-2 text-xs font-semibold text-[var(--gv-ink-muted)] transition-colors hover:bg-stone-100"
              >
                <Phone className="h-3.5 w-3.5 text-emerald-600" />
                <span>Call Parent</span>
              </a>
              <a
                href={`https://wa.me/${viewingProfileStudent.parentMobile.replace(/[^0-9]/g, '')}?text=Jai%20Jinendra%20from%20Jain%20Pathshala!`}
                target="_blank"
                rel="noreferrer"
                className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-[var(--gv-border)] bg-white p-2 text-xs font-semibold text-[var(--gv-ink-muted)] transition-colors hover:bg-stone-100"
              >
                <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
                <span>WhatsApp</span>
              </a>
              <a
                href={`mailto:${viewingProfileStudent.email}`}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-[var(--gv-border)] bg-white p-2 text-xs font-semibold text-[var(--gv-ink-muted)] transition-colors hover:bg-stone-100"
              >
                <Mail className="h-3.5 w-3.5 text-[var(--gv-brand)]" />
                <span>Email</span>
              </a>
            </div>

            {/* Profile Grid */}
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-4">
                <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Academic Level</span>
                <p className="font-bold text-[var(--gv-ink)]">{viewingProfileStudent.level}</p>
                <p className="text-[11px] text-[var(--gv-ink-muted)]">{viewingProfileStudent.batch}</p>
              </div>

              <div className="space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-4">
                <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Attendance & Points</span>
                <p className="font-bold text-emerald-700">{viewingProfileStudent.attendanceRate}% Attendance</p>
                <p className="text-[11px] font-bold text-amber-700">{viewingProfileStudent.pointsBalance} Reward Points</p>
              </div>

              <div className="col-span-2 space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-4">
                <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Residential Address</span>
                <p className="font-medium text-[var(--gv-ink-muted)]">{viewingProfileStudent.address}</p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="secondary" size="sm" onClick={() => setViewingProfileStudent(null)}>
                Close Profile
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
